"use client"

import { useTagProposals } from "@/lib/use-tag-proposals"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ThumbsUp, ThumbsDown } from "@/lib/icons"
import { toast } from "sonner"
import type React from "react"
import { Button } from "@/components/ui/button"
import { useTranslations } from "next-intl"

interface DisputesPanelProps {
  contentId: string
}

// Shows every "this tag doesn't belong here" proposal for a post — approved
// ones (already hidden from the tag list above) and pending ones anyone can
// still vote on. Real, shared voting (see lib/use-tag-proposals.ts); this
// used to be entirely localStorage-only.
export function DisputesPanel({ contentId }: DisputesPanelProps) {
  const t = useTranslations("disputesPanel")
  const { proposals, approveThreshold, vote } = useTagProposals(contentId)

  const disputes = proposals.filter((p) => p.kind === "remove")
  const approvedDisputes = disputes.filter((d) => d.status === "approved")
  const pendingDisputes = disputes.filter((d) => d.status === "pending")

  const castVote = async (disputeId: string, value: 1 | -1 | 0, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const result = await vote(disputeId, value)
    if (!result.ok && result.error === "sign-in-required") toast.error(t("signInToVote"))
    else if (!result.ok) toast.error(result.error || t("somethingWentWrong"))
  }

  if (disputes.length === 0) return null

  return (
    <div className="space-y-4 border-t px-2 pt-0 mt-2">
      <h3 className="text-sm font-semibold text-muted-foreground">{t("tagDisputes")}</h3>

      {approvedDisputes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-orange-600">{t("disputedTagsHidden")}</h4>
          <div className="flex flex-wrap gap-2">
            {approvedDisputes.map((dispute) => (
              <Badge key={dispute.id} className="bg-orange-500/20 text-orange-700 line-through">
                {dispute.tag.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {pendingDisputes.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold">{t("pendingDisputes")}</h4>
          <ScrollArea className="h-32">
            <div className="space-y-2 pr-4">
              {pendingDisputes.map((dispute) => (
                <div key={dispute.id} className="flex items-center justify-between p-2 rounded-md bg-secondary/20">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {dispute.tag.replace(/_/g, " ")}
                    </Badge>
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span className="text-green-600">{t("upvotesValueApproveAt", { upvotes: dispute.upvotes, approveThreshold })}</span>
                      <span className="text-red-600">{t("downvotesValue", { downvotes: dispute.downvotes })}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={dispute.myVote === 1 ? "default" : "ghost"}
                      className={`h-6 w-6 p-0 ${dispute.myVote === 1 ? "bg-primary text-primary-foreground" : ""}`}
                      onClick={(e) => castVote(dispute.id, dispute.myVote === 1 ? 0 : 1, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <ThumbsUp className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={dispute.myVote === -1 ? "default" : "ghost"}
                      className={`h-6 w-6 p-0 ${dispute.myVote === -1 ? "bg-primary text-primary-foreground" : ""}`}
                      onClick={(e) => castVote(dispute.id, dispute.myVote === -1 ? 0 : -1, e)}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <ThumbsDown className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  )
}
